import React, { useState, useEffect, useContext, useRef } from "react";
import PropTypes from "prop-types";
import {
	Text,
	CloudinaryImage,
	Grid,
	Col,
	Card,
	LineGraphic,
	CommonLines,
	Button,
} from "@website-builder/ui/shared/elements";
import { navigate } from "gatsby";
import axios from "axios";
import {
	addToDataLayer,
	handleCTAClick,
	INVITE_TYPE,
} from "@website-builder/utilities-index";
import {
	FacebookShareButton,
	TwitterShareButton,
	LinkedinShareButton,
	RedditShareButton,
	EmailShareButton,
} from "react-share";
import { UserContext } from "../../Layout/HeaderFooter/index";
import {
	Container,
	CodeBox,
	CopyIcon,
	SocialShare,
	SocialMedia,
	OverlappingCardWrapper,
	FormContainer,
	Form,
} from "./styles";

const InviteHeroBanner = (props) => {
	const {
		pageType,
		desktopBgImageUrl,
		mobileBgImageUrl,
		header,
		description,
		socialShareUrl,
		cta,
		image,
		card,
		hsForm,
		context,
		pageSection,
	} = props;

	const { apiBaseUrl } = context;

	const userDetails = useContext(UserContext);
	const referralCodeLink = userDetails?.referral_code_link || "";
	const [clicked, setClicked] = useState(false);

	const [referralDetails, setReferralDetails] = useState(null);
	const [referralCode, setReferralCode] = useState(null);

	useEffect(() => {
		if (
			pageType === INVITE_TYPE.REFEREE &&
			process.env.GATSBY_TEMPLATE === "STATIC_SITE"
		) {
			//Keep Referral code
			const params = new URL(document?.location)?.searchParams;
			const refCode = params?.get("code")?.replace("/", "");
			setReferralCode(refCode);

			const baseUrl =
				apiBaseUrl ||
				`${window?.location?.protocol}//${window?.location?.host}`;

			axios
				.get(`${baseUrl}/referral/coupon-info/${refCode}/`)
				.then((r) => {
					setReferralDetails(r.data);
				})
				.catch((err) => {
					if (err.response.status == 400) {
						console.error("Not a valid referral");
						navigate("/404/");
					}
				});
		}

		if (hsForm && typeof window !== "undefined") {
			const script = document.createElement("script");
			script.src = "https://js.hsforms.net/forms/v2.js";
			document.body.appendChild(script);

			script.addEventListener("load", () => {
				if (window.hbspt) {
					window.hbspt.forms.create({
						region: "na1",
						portalId: hsForm.portalId || "2784014",
						formId: hsForm.formId || "8f5a3e24-a18c-4275-a0f8-4c3becf38f15",
						target: "#hubspotForm",
					});
				}
			});
		}
	}, []);

	useEffect(() => {
		if (referralDetails) {
			const { promoter_email, referral_amount } = referralDetails;
			addToDataLayer("friend_referral_code_access", {
				referrer_identifier: promoter_email,
				referral_code: referralCode,
				discount_amount: referral_amount,
			});

			localStorage.setItem("referrer_identifier", promoter_email);
			localStorage.setItem("referral_code", referralCode);
		}
	}, [referralDetails]);

	const isEventDuplicate = (platform_name) => {
		const sessionData = JSON.parse(sessionStorage.getItem("social_share"));
		if (sessionData && sessionData.includes(platform_name)) {
			return true;
		}

		const prevData = JSON.parse(sessionStorage.getItem("social_share"));
		if (prevData == null) {
			sessionStorage.setItem("social_share", JSON.stringify([platform_name]));
		} else {
			prevData.push(platform_name);
			sessionStorage.setItem("social_share", JSON.stringify(prevData));
		}
		return false;
	};

	//segment tracking for social share
	const socialSegmentTrackEvent = (eventName, { platform_name }) => {
		let inviteCode = referralCodeLink;

		if (!isEventDuplicate(platform_name)) {
			if (inviteCode[inviteCode?.length - 1] == "/") {
				inviteCode = inviteCode?.slice(0, -1);
			}

			if (platform_name === "copyIcon") {
				addToDataLayer({
					event: eventName,
					referral_code: inviteCode?.split("/").slice(-1).pop(),
				});
			} else {
				addToDataLayer({
					event: eventName,
					platform_name: platform_name,
					referral_code: inviteCode?.split("/").slice(-1).pop(),
				});
			}
		}
	};

	const replaceName = (content) => {
		if (pageType === INVITE_TYPE.REFERRER) {
			const referrerName = userDetails?.firstname || "";
			const text = content.replace(/{{referrer}}/gi, referrerName);
			return text;
		}

		if (pageType === INVITE_TYPE.REFEREE) {
			const referrerName = referralDetails?.promoter_first_name || "";
			let text = content.replace(/{{referrer}}/gi, referrerName);

			const refereeName = userDetails?.firstname || "";
			text = text.replace(/{{referee}}/gi, refereeName);
			return text;
		}
	};

	const replaceAmount = (content) => {
		const promoterAmount = userDetails?.promoter_referral_amount || 250;
		let referralAmount;
		if (pageType === INVITE_TYPE.REFEREE) {
			referralAmount = referralDetails?.referral_amount;
		} else {
			referralAmount = userDetails?.referral_amount;
		}
		let text = content.replace(/{{promoter_amount}}/gi, promoterAmount);
		text = text.replace(/{{referral_amount}}/gi, referralAmount || 750);
		return text;
	};

	const replaceCode = (content) => {
		let inviteCode = "";
		if (pageType === INVITE_TYPE.REFERRER) {
			inviteCode = referralCodeLink?.split("/").slice(-1).pop();
		} else {
			inviteCode = referralCode;
		}
		const text = content.replace(/{{code}}/gi, inviteCode);
		return text;
	};

	const formattedHeader = replaceName(header);
	let formattedDescription = replaceName(description);
	formattedDescription = replaceAmount(formattedDescription);
	formattedDescription = replaceCode(formattedDescription);

	const referralInviteUrl = socialShareUrl
		? `${socialShareUrl}${referralCodeLink?.split("/").slice(-1).pop()}`
		: referralCodeLink;

	const handleCopyClick = () => {
		if (pageType === INVITE_TYPE.REFERRER) {
			navigator.clipboard.writeText(referralInviteUrl);
			socialSegmentTrackEvent("Referrer Invite Link Copied", {
				platform_name: "copyIcon",
			});
		} else {
			navigator.clipboard.writeText(referralCode);
			socialSegmentTrackEvent("Referral Code Copied", {
				platform_name: "copyIcon",
			});
		}
		setClicked(true);
	};

	const copyText =
		pageType === INVITE_TYPE.REFERRER ? "Copy Link" : "Copy Code";

	const codeBoxJSX = (
		<div>
			<CodeBox>
				{pageType === INVITE_TYPE.REFERRER ? (
					<Text variant="paragraph_M_bold" className="invite-link">
						{socialShareUrl
							? `${socialShareUrl}${referralCodeLink
									?.split("/")
									.slice(-1)
									.pop()}`
							: referralCodeLink}
					</Text>
				) : (
					<>
						<Text variant="headline_4" className="referral-code">
							{referralCode}
						</Text>
					</>
				)}
				<CopyIcon onClick={handleCopyClick}>
					<CloudinaryImage
						url={
							"https://res.cloudinary.com/springboard-images/image/upload/v1636436778/Screenshot_2021-11-09_at_11.15.32_AM.png"
						}
						lazyload
						sizes={{
							default: { height: 25 },
						}}
					/>
					{clicked ? "Copied" : copyText}
				</CopyIcon>
			</CodeBox>
			{pageType === INVITE_TYPE.REFEREE ? (
				<Text style={{ marginTop: "4px" }}>Use this code at checkout.</Text>
			) : null}
		</div>
	);

	return (
		<>
			<Container
				desktopBgImageUrl={desktopBgImageUrl}
				mobileBgImageUrl={mobileBgImageUrl}
			>
				<Grid>
					<Col start={1} span={5} mdSpan={6} className="content-wrapper">
						<Text variant="headline_3">{formattedHeader}</Text>
						<Text
							variant="paragraph_L"
							tag="div"
							dangerouslySetInnerHTML={{ __html: formattedDescription }}
						/>
						{pageType === INVITE_TYPE.REFEREE ? codeBoxJSX : null}
						{cta ? (
							<div>
								<Button
									buttonText={cta?.buttonText}
									type={cta?.type}
									isDarkBg={cta?.isDarkBg}
									disabled={cta?.disabled}
									onClick={() =>
										handleCTAClick(cta, {
											page_section: pageSection,
											click_text: cta?.buttonText,
											redirect_to: cta?.link?.link,
										})
									}
								/>
							</div>
						) : null}
					</Col>
					<Col start={7} span={6} mdSpan={6} className="image-wrapper">
						<CloudinaryImage url={image?.url} alt={image?.alt} lazyload />
					</Col>
				</Grid>
			</Container>
			{card ? (
				<OverlappingCardWrapper
					isDarkBackground={card.isDarkBg}
					topBackgroundColor={card.topBackgroundColor}
					bottomBackgroundColor={card.bottomBackgroundColor}
					contentBackgroundColor={card.contentBackgroundColor}
				>
					<LineGraphic
						className="course-line-graphic"
						stroke="var(--primary-green-400)"
						size="238px"
						thickness="2px"
						left="19%"
						top="25%"
						rotate="75"
						line="offset_loop"
					/>
					<Grid className="grid-container">
						<Col start={3} span={8} mdStart={1} mdSpan={6}>
							<Card className="card-container">
								<div className="description-wrapper">
									<Text dark={card.isDarkBg} variant={"headline_4"}>
										{card.title}
									</Text>
								</div>
								{pageType === INVITE_TYPE.REFERRER ? codeBoxJSX : null}
								<SocialShare>
									<SocialMedia
										onClick={() =>
											socialSegmentTrackEvent("social_share_clicked", {
												platform_name: "Facebook",
											})
										}
									>
										<FacebookShareButton url={referralInviteUrl}>
											<CloudinaryImage
												url={
													"https://res.cloudinary.com/springboard-images/image/upload/v1692719863/Marketing_Creative_Assets/100%20Consumer/Updated%20Referral%20Page/facebook_icon_mobile.png"
												}
												lazyload
												sizes={{
													default: { height: 60 },
												}}
											/>
										</FacebookShareButton>
									</SocialMedia>

									<SocialMedia
										onClick={() =>
											socialSegmentTrackEvent("social_share_clicked", {
												platform_name: "Twitter",
											})
										}
									>
										<TwitterShareButton url={referralInviteUrl}>
											<CloudinaryImage
												url={
													"https://res.cloudinary.com/springboard-images/image/upload/v1692719908/Marketing_Creative_Assets/100%20Consumer/Updated%20Referral%20Page/Twitter_icon.png"
												}
												lazyload
												sizes={{
													default: { height: 60 },
												}}
											/>
										</TwitterShareButton>
									</SocialMedia>

									<SocialMedia
										onClick={() =>
											socialSegmentTrackEvent("social_share_clicked", {
												platform_name: "Linkedin",
											})
										}
									>
										<LinkedinShareButton url={referralInviteUrl}>
											<CloudinaryImage
												url={
													"https://res.cloudinary.com/springboard-images/image/upload/v1692719896/Marketing_Creative_Assets/100%20Consumer/Updated%20Referral%20Page/linkedin_icon_mobile.png"
												}
												lazyload
												sizes={{
													default: { height: 60 },
												}}
											/>
										</LinkedinShareButton>
									</SocialMedia>

									<SocialMedia
										onClick={() =>
											socialSegmentTrackEvent("social_share_clicked", {
												platform_name: "Reddit",
											})
										}
									>
										<RedditShareButton url={referralInviteUrl}>
											<CloudinaryImage
												url={
													"https://res.cloudinary.com/springboard-images/image/upload/v1692719901/Marketing_Creative_Assets/100%20Consumer/Updated%20Referral%20Page/reddit_icon_mobile.png"
												}
												lazyload
												sizes={{
													default: { height: 60 },
												}}
											/>
										</RedditShareButton>
									</SocialMedia>

									<SocialMedia
										onClick={() =>
											socialSegmentTrackEvent("social_share_clicked", {
												platform_name: "Email",
											})
										}
									>
										<EmailShareButton url={referralInviteUrl}>
											<CloudinaryImage
												url={
													"https://res.cloudinary.com/springboard-images/image/upload/v1692719855/Marketing_Creative_Assets/100%20Consumer/Updated%20Referral%20Page/email_icon_mobile.png"
												}
												lazyload
												sizes={{
													default: { height: 60 },
												}}
											/>
										</EmailShareButton>
									</SocialMedia>
								</SocialShare>
							</Card>
						</Col>
					</Grid>
				</OverlappingCardWrapper>
			) : null}
			{hsForm ? (
				<FormContainer>
					<Form darkBg={true}>
						<Text variant="headline_4" className="invite-form-title">
							{hsForm.title}
						</Text>
						<Text
							variant="paragraph_M"
							tag="div"
							dangerouslySetInnerHTML={{ __html: hsForm.description }}
							className="invite-form-description"
						/>
						<div className="hubspotForms" id="hubspotForm"></div>
					</Form>
				</FormContainer>
			) : null}
		</>
	);
};

InviteHeroBanner.propTypes = {
	pageType: PropTypes.string,
	desktopBgImageUrl: PropTypes.string,
	mobileBgImageUrl: PropTypes.string,
	header: PropTypes.string,
	description: PropTypes.string,
	socialShareUrl: PropTypes.string,
	cta: PropTypes.object,
	image: PropTypes.object,
	card: PropTypes.object,
	hsForm: PropTypes.object,
};

InviteHeroBanner.defaultProps = {
	pageType: "",
	desktopBgImageUrl: "",
	mobileBgImageUrl: "",
	header: "",
	description: "",
	socialShareUrl: "",
	image: {},
};

export default InviteHeroBanner;
